import APIService from "./APIService";

/**
 * API service for the Alerts Intelligence Hub.
 * Provides methods for interacting with alert intelligence endpoints.
 */
class AlertIntelligenceAPI {
  static BASE_PATH = "/api/v1/app/alerts/intelligence";

  // ═══════════════════════════════════════════════════════════════════════════
  // DASHBOARD
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Get the main dashboard data for the Alerts Intelligence Hub.
   * Returns summary stats, featured alerts, recent alerts, and user preferences.
   *
   * @returns {Promise<Object>} Dashboard data with stats, alerts, and preferences
   */
  async getDashboard() {
    try {
      const response = await APIService.get(
        `${AlertIntelligenceAPI.BASE_PATH}/dashboard`,
      );
      return response;
    } catch (error) {
      console.error("Error fetching alerts dashboard:", error);
      throw error;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SEARCH & BROWSE
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Search alerts with filters and pagination.
   *
   * @param {Object} params - Search parameters
   * @param {string} [params.searchText] - Free text search
   * @param {Array<string>} [params.categories] - Filter by categories
   * @param {number} [params.minThreatLevel] - Minimum threat level (1-10)
   * @param {number} [params.maxThreatLevel] - Maximum threat level (1-10)
   * @param {Array<string>} [params.regions] - Filter by geographic regions
   * @param {Array<string>} [params.tags] - Filter by tags
   * @param {boolean} [params.peakActivityOnly] - Show only peak activity alerts
   * @param {boolean} [params.unreviewedOnly] - Show only unreviewed alerts
   * @param {boolean} [params.withTrainingOnly] - Show only alerts with training
   * @param {string} [params.sortBy] - Sort field (THREAT_LEVEL, RECENT, VIEW_COUNT, SHARE_COUNT)
   * @param {string} [params.sortDirection] - Sort direction (ASC, DESC)
   * @param {number} [params.page] - Page number (0-indexed)
   * @param {number} [params.size] - Page size (default 20, max 100)
   * @returns {Promise<Object>} Paginated search results
   */
  async searchAlerts(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      // Ensure searchText is not duplicated (guard against double-submission)
      if (params.searchText) {
        const searchText = String(params.searchText).trim();
        // If searchText contains a comma, it might be duplicated - take first part
        const cleanSearchText = searchText.includes(",")
          ? searchText.split(",")[0].trim()
          : searchText;
        if (cleanSearchText) {
          queryParams.append("searchText", cleanSearchText);
        }
      }
      if (params.categories?.length)
        queryParams.append("categories", params.categories.join(","));
      if (params.minThreatLevel)
        queryParams.append("minThreatLevel", params.minThreatLevel);
      if (params.maxThreatLevel)
        queryParams.append("maxThreatLevel", params.maxThreatLevel);
      if (params.regions?.length)
        queryParams.append("regions", params.regions.join(","));
      if (params.tags?.length)
        queryParams.append("tags", params.tags.join(","));
      if (params.peakActivityOnly)
        queryParams.append("peakActivityOnly", "true");
      if (params.unreviewedOnly) queryParams.append("unreviewedOnly", "true");
      if (params.withTrainingOnly)
        queryParams.append("withTrainingOnly", "true");
      if (params.sortBy) queryParams.append("sortBy", params.sortBy);
      if (params.sortDirection)
        queryParams.append("sortDirection", params.sortDirection);
      if (params.page !== undefined) queryParams.append("page", params.page);
      if (params.size) queryParams.append("size", params.size);

      const queryString = queryParams.toString();
      const url = `${AlertIntelligenceAPI.BASE_PATH}/search${queryString ? `?${queryString}` : ""}`;

      console.log("[AlertIntelligenceAPI] Search URL:", url);

      const response = await APIService.get(url);
      return response;
    } catch (error) {
      console.error("Error searching alerts:", error);
      throw error;
    }
  }

  /**
   * Get all available alert categories.
   *
   * @returns {Promise<Array>} Array of categories with name, displayName, icon, description
   */
  async getCategories() {
    try {
      const response = await APIService.get(
        `${AlertIntelligenceAPI.BASE_PATH}/categories`,
      );
      return response;
    } catch (error) {
      console.error("Error fetching categories:", error);
      throw error;
    }
  }

  /**
   * Get alerts by category.
   * Convenience method for filtering by single category.
   *
   * @param {string} category - Category name
   * @param {Object} params - Additional search parameters
   * @returns {Promise<Object>} Paginated alerts for category
   */
  async getAlertsByCategory(category, params = {}) {
    return this.searchAlerts({
      ...params,
      categories: [category],
    });
  }

  /**
   * Get critical alerts (threat level >= 8).
   *
   * @param {Object} params - Additional search parameters
   * @returns {Promise<Object>} Paginated critical alerts
   */
  async getCriticalAlerts(params = {}) {
    return this.searchAlerts({
      ...params,
      minThreatLevel: 8,
      sortBy: "THREAT_LEVEL",
      sortDirection: "DESC",
    });
  }

  /**
   * Get alerts at peak activity.
   *
   * @param {Object} params - Additional search parameters
   * @returns {Promise<Object>} Paginated peak activity alerts
   */
  async getPeakActivityAlerts(params = {}) {
    return this.searchAlerts({
      ...params,
      peakActivityOnly: true,
    });
  }

  /**
   * Get unreviewed alerts for the user.
   *
   * @param {Object} params - Additional search parameters
   * @returns {Promise<Object>} Paginated unreviewed alerts
   */
  async getUnreviewedAlerts(params = {}) {
    return this.searchAlerts({
      ...params,
      unreviewedOnly: true,
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ALERT DETAIL
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Get full alert detail including educational content and user state.
   * Automatically records a view engagement.
   *
   * @param {number} alertId - Alert ID
   * @returns {Promise<Object>} Full alert detail
   */
  async getAlertDetail(alertId) {
    try {
      const response = await APIService.get(
        `${AlertIntelligenceAPI.BASE_PATH}/${alertId}`,
      );
      return response;
    } catch (error) {
      console.error("Error fetching alert detail:", error);
      throw error;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ENGAGEMENT TRACKING
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Record user engagement with an alert.
   *
   * @param {number} alertId - Alert ID
   * @param {string} engagementType - Type: VIEWED, MARKED_REVIEWED, SHARED, TRAINING_STARTED, DISMISSED
   * @param {Object} [options] - Additional options
   * @param {string} [options.notes] - Optional notes
   * @param {string} [options.trainingUuid] - Training UUID (for TRAINING_STARTED)
   * @returns {Promise<Object>} Engagement result
   */
  async recordEngagement(alertId, engagementType, options = {}) {
    try {
      const response = await APIService.post(
        `${AlertIntelligenceAPI.BASE_PATH}/engagement`,
        {
          alertId,
          engagementType,
          notes: options.notes,
          trainingUuid: options.trainingUuid,
        },
      );
      return response;
    } catch (error) {
      console.error("Error recording engagement:", error);
      throw error;
    }
  }

  /**
   * Mark an alert as reviewed.
   *
   * @param {number} alertId - Alert ID
   * @returns {Promise<Object>} Result
   */
  async markAsReviewed(alertId) {
    try {
      const response = await APIService.post(
        `${AlertIntelligenceAPI.BASE_PATH}/${alertId}/mark-reviewed`,
      );
      return response;
    } catch (error) {
      console.error("Error marking alert as reviewed:", error);
      throw error;
    }
  }

  /**
   * Record that user started related training.
   *
   * @param {number} alertId - Alert ID
   * @param {string} trainingUuid - Training UUID
   * @returns {Promise<Object>} Result
   */
  async recordTrainingStarted(alertId, trainingUuid) {
    return this.recordEngagement(alertId, "TRAINING_STARTED", { trainingUuid });
  }

  /**
   * Dismiss an alert (user doesn't want to see it prominently).
   *
   * @param {number} alertId - Alert ID
   * @returns {Promise<Object>} Result
   */
  async dismissAlert(alertId) {
    return this.recordEngagement(alertId, "DISMISSED");
  }

  /**
   * Get user's engagement statistics.
   *
   * @returns {Promise<Object>} User stats including views, reviews, shares, and history
   */
  async getUserStats() {
    try {
      const response = await APIService.get(
        `${AlertIntelligenceAPI.BASE_PATH}/stats`,
      );
      return response;
    } catch (error) {
      console.error("Error fetching user stats:", error);
      throw error;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // FAMILY SHARING
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Share an alert with all family members.
   *
   * SECURITY: Only sends alertId, message, and scan context to the backend.
   * The backend determines the user's group and family members from
   * the authenticated session - no group IDs or member UUIDs are sent.
   *
   * @param {number} alertId - Alert ID to share
   * @param {Object} [options] - Share options
   * @param {string} [options.message] - Optional personal message
   * @param {string} [options.scanType] - Type of scan (e.g. "SMS Scam", "Email Scam")
   * @param {string} [options.scanTitle] - Title describing the detected threat
   * @param {string} [options.scanDetail] - Detail about the scanned content
   * @returns {Promise<Object>} Share result with recipient first names
   */
  async shareAlertWithFamily(alertId, options = {}) {
    try {
      const response = await APIService.post(
        `${AlertIntelligenceAPI.BASE_PATH}/share`,
        {
          alertId,
          message: options.message || null,
          scanType: options.scanType || null,
          scanTitle: options.scanTitle || null,
          scanDetail: options.scanDetail || null,
        },
      );
      return response;
    } catch (error) {
      console.error("Error sharing alert:", error);
      throw error;
    }
  }

  /**
   * Mark a shared alert as viewed.
   *
   * @param {number} shareId - Share record ID
   * @returns {Promise<Object>} Result
   */
  async markShareAsViewed(shareId) {
    try {
      const response = await APIService.post(
        `${AlertIntelligenceAPI.BASE_PATH}/share/${shareId}/viewed`,
      );
      return response;
    } catch (error) {
      console.error("Error marking share as viewed:", error);
      throw error;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // USER PREFERENCES
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Get user's alert preferences.
   *
   * @returns {Promise<Object>} User preferences
   */
  async getPreferences() {
    try {
      const response = await APIService.get(
        `${AlertIntelligenceAPI.BASE_PATH}/preferences`,
      );
      return response;
    } catch (error) {
      console.error("Error fetching preferences:", error);
      throw error;
    }
  }

  /**
   * Update user's alert preferences.
   *
   * @param {Object} preferences - Preferences to update
   * @param {Array<string>} [preferences.subscribedCategories] - Categories to subscribe to
   * @param {Array<string>} [preferences.geographicInterest] - Geographic regions of interest
   * @param {string} [preferences.notificationFrequency] - IMMEDIATE, DAILY, WEEKLY, NONE
   * @param {boolean} [preferences.inAppNotificationsEnabled] - Enable in-app notifications
   * @param {string} [preferences.defaultSortOrder] - THREAT_LEVEL, RECENT, CATEGORY
   * @param {boolean} [preferences.showReviewedAlerts] - Show already reviewed alerts
   * @returns {Promise<Object>} Updated preferences
   */
  async updatePreferences(preferences) {
    try {
      const response = await APIService.put(
        `${AlertIntelligenceAPI.BASE_PATH}/preferences`,
        preferences,
      );
      return response;
    } catch (error) {
      console.error("Error updating preferences:", error);
      throw error;
    }
  }

  /**
   * Subscribe to specific categories.
   *
   * @param {Array<string>} categories - Category names to subscribe to
   * @returns {Promise<Object>} Updated preferences
   */
  async subscribeToCategories(categories) {
    return this.updatePreferences({ subscribedCategories: categories });
  }

  /**
   * Set notification frequency.
   *
   * @param {string} frequency - IMMEDIATE, DAILY, WEEKLY, or NONE
   * @returns {Promise<Object>} Updated preferences
   */
  async setNotificationFrequency(frequency) {
    return this.updatePreferences({ notificationFrequency: frequency });
  }

  /**
   * Toggle in-app notifications.
   *
   * @param {boolean} enabled - Enable or disable
   * @returns {Promise<Object>} Updated preferences
   */
  async setInAppNotifications(enabled) {
    return this.updatePreferences({ inAppNotificationsEnabled: enabled });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // UTILITY METHODS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Get threat level label from numeric value.
   *
   * @param {number} threatLevel - Threat level (1-10)
   * @returns {string} Label: LOW, MODERATE, HIGH, or CRITICAL
   */
  getThreatLevelLabel(threatLevel) {
    if (threatLevel >= 8) return "CRITICAL";
    if (threatLevel >= 6) return "HIGH";
    if (threatLevel >= 4) return "MODERATE";
    return "LOW";
  }

  /**
   * Get threat level color from numeric value.
   *
   * @param {number} threatLevel - Threat level (1-10)
   * @returns {string} Hex color code
   */
  getThreatLevelColor(threatLevel) {
    if (threatLevel >= 8) return "#DC2626"; // red
    if (threatLevel >= 6) return "#F97316"; // orange
    if (threatLevel >= 4) return "#EAB308"; // yellow
    return "#22C55E"; // green
  }

  /**
   * Format relative time for display (e.g., "3 days ago").
   *
   * @param {string} dateString - ISO date string
   * @returns {string} Relative time string
   */
  formatRelativeTime(dateString) {
    if (!dateString) return "";

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  }
}

// Export singleton instance
const alertIntelligenceAPI = new AlertIntelligenceAPI();
export default alertIntelligenceAPI;

// Also export class for testing
export { AlertIntelligenceAPI };
